"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import {
  LandingAuthModals,
  LandingBenefits,
  LandingFeatures,
  LandingFooter,
  LandingHeader,
  LandingHero,
  LandingHowItWorks,
  LandingTestimonials,
  MaintenanceBanner,
} from "@/components/domain";
import {
  fetchFrontendConfig,
  type FrontendConfig,
} from "@/services/api/info";

const HERO_IMAGE = "/landing/hero.jpg";
const TEAM_IMAGE = "/landing/team.jpg";

export default function RootPage() {
  const searchParams = useSearchParams();
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const referralCode =
    searchParams.get("ref") ?? searchParams.get("ref_code") ?? "";
  const [cfg, setCfg] = useState<FrontendConfig | null>(null);

  useEffect(() => {
    void (async () => {
      const [data] = await fetchFrontendConfig();
      if (data) setCfg(data);
    })();
  }, []);

  const registrationEnabled = cfg?.flags?.registration_enabled !== false;

  useEffect(() => {
    const modal = searchParams.get("modal");
    if (modal === "login") setLoginOpen(true);
    if (modal === "register" && registrationEnabled) setRegisterOpen(true);
  }, [searchParams, registrationEnabled]);

  const handleSwitchToRegister = () => {
    if (!registrationEnabled) return;
    setLoginOpen(false);
    setRegisterOpen(true);
  };

  const handleSwitchToLogin = () => {
    setRegisterOpen(false);
    setLoginOpen(true);
  };

  const openRegister = () => {
    if (!registrationEnabled) return;
    setRegisterOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <MaintenanceBanner />
      <LandingHeader
        authOpen={loginOpen || registerOpen}
        onLoginClick={() => setLoginOpen(true)}
        onRegisterClick={openRegister}
      />
      <main>
        <LandingHero
          ctaPrimary={cfg?.landing?.cta_primary}
          ctaSecondary={cfg?.landing?.cta_secondary}
          headline={cfg?.landing?.headline}
          heroImage={HERO_IMAGE}
          onLoginClick={() => setLoginOpen(true)}
          onRegisterClick={openRegister}
          subheadline={cfg?.landing?.subheadline}
        />
        <LandingFeatures />
        <LandingHowItWorks onRegisterClick={openRegister} />
        <LandingBenefits
          onRegisterClick={openRegister}
          teamImage={TEAM_IMAGE}
        />
        <LandingTestimonials />
      </main>
      <LandingFooter
        discordUrl={cfg?.links?.discord_url}
        supportUrl={cfg?.links?.support_url}
        telegramUrl={cfg?.links?.telegram_url}
      />

      <LandingAuthModals
        loginOpen={loginOpen}
        onLoginOpenChange={setLoginOpen}
        onRegisterOpenChange={setRegisterOpen}
        onSwitchToLogin={handleSwitchToLogin}
        onSwitchToRegister={handleSwitchToRegister}
        referralCode={referralCode}
        registerOpen={registerOpen && registrationEnabled}
      />
    </div>
  );
}
